import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';
import { useState, useCallback } from 'react';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { ApiException, SimulationDataSetDto } from '@/api/ApiClient';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';

interface ReaxffDownloadDialogProps {
  dataset: SimulationDataSetDto;
  /** Whether the dialog is currently open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** Callback when download is completed, receives the user ID from backend */
  onDownloadComplete: (userId: string) => void;
}

interface StoredUserInfo {
  /** User's email address */
  email: string;
  /** User's full name */
  name: string;
  /** User's institution or organization */
  institution: string;
}

/** Key used to store user info in localStorage */
const STORAGE_KEY = 'reaxff_user_info';

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const getStoredUserInfo = (): StoredUserInfo | null => {
  if (typeof window === 'undefined') return null;
  const stored = scopedLocalStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const storeUserInfo = (info: StoredUserInfo) => {
  if (typeof window === 'undefined') return;
  scopedLocalStorage.setItem(STORAGE_KEY, JSON.stringify(info));
};

type ReaxffDownloadDialogBodyProps = {
  dataset: SimulationDataSetDto;
  onClose: () => void;
  onDownloadComplete: (userId: string) => void;
};

function ReaxffDownloadDialogBody({ dataset, onClose, onDownloadComplete }: ReaxffDownloadDialogBodyProps) {
  const [email, setEmail] = useState(() => getStoredUserInfo()?.email ?? '');
  const [name, setName] = useState(() => getStoredUserInfo()?.name ?? '');
  const [institution, setInstitution] = useState(() => getStoredUserInfo()?.institution ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const validateForm = (): boolean => {
    if (!email || !name || !institution) {
      showSnackbar('Please fill in all fields', 'error');
      return false;
    }

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const userInfo = {
        email: email.trim(),
        name: name.trim(),
        institution: institution.trim(),
      };

      storeUserInfo(userInfo);

      const userId = await backend.simulationDataset.setDownloadUser(userInfo);

      if (!userId) {
        throw new Error('No user ID received from server');
      }

      log.info('[ReaxffDownloadDialog] Download user registration successful:', {
        userId,
        userInfo,
      });

      onDownloadComplete(userId);
      onClose();
    } catch (error: any) {
      if (error instanceof ApiException) {
        switch (error.status) {
          case 400:
            setEmailError('Invalid email format or missing required fields');
            showSnackbar('Invalid user information provided. Please check your input.', 'error');
            break;
          case 500:
            showSnackbar('Server error occurred. Please try again later.', 'error');
            break;
          default:
            showSnackbar(`Failed to register user: ${getProblemDetail(error) ?? 'Unknown error'}`, 'error');
        }
      } else {
        showSnackbar(`Failed to process download request: ${getProblemDetail(error) ?? 'Unknown error'}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      showSnackbar('Please wait while we process your request', 'info');
      return;
    }
    onClose();
  };

  return (
    <>
      <DialogTitle>Download ReaxFF Force Field</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You are downloading <strong>{dataset.filename}</strong>, a ReaxFF force field parameter file. Please provide
          your name, email, and institution. This helps us track usage and improve support for the scientific community.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
          disabled={isSubmitting}
        />
        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError('');
          }}
          error={!!emailError}
          helperText={emailError}
          sx={{ mb: 2 }}
          disabled={isSubmitting}
        />
        <TextField
          margin="dense"
          label="Institution"
          type="text"
          fullWidth
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          disabled={isSubmitting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={isSubmitting || !email || !name || !institution}
        >
          {isSubmitting ? 'Processing...' : 'Download'}
        </Button>
      </DialogActions>
    </>
  );
}

export function ReaxffDownloadDialog({ dataset, open, onClose, onDownloadComplete }: ReaxffDownloadDialogProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose}>
      {open ? (
        <ReaxffDownloadDialogBody
          key={dataset.filename}
          dataset={dataset}
          onClose={handleClose}
          onDownloadComplete={onDownloadComplete}
        />
      ) : null}
    </Dialog>
  );
}
